/**
 * People's and buildings' names are never translated. Every answer the API client gets passes through
 * noteAnswer(): while English is on, the last few answers are only remembered (no work);
 * once Spanish loads, they and every later answer are walked for person-name fields and
 * those names are protected in the engine (a resident called "May" or "Wing Allen" stays
 * exactly that). Bounded: a walk stops after a few thousand values.
 */

// Keys whose value is a person's name. A bare `name` counts only beside a person's other
// fields (a room, an email, a role, a phone), so a section or rule called "name" is not one.
const PERSON_KEY =
  /^(?:first|last|full|preferred|display|given|family)_?name$|(?:resident|staff|user|member|caregiver|nurse|person|patient|contact|actor|author|signer|signed_?by|owner|assignee|assigned_?to|aide|requester|reviewer|approver|sender|from|by|subject)_?name$/i;
const PERSON_SIBLING = /^(?:room|email|role|phone|dob|resident_?id|user_?id|care_?title|initials)$/i;
// A building's name is a name too ("Arbour at Cedar Park" stays as it is). Only the whole
// name is protected, never its first word ("The", "Sunrise" are words on their own).
const BUILDING_KEY = /^(?:facility|building|org|organization|community|operator)_?name$/i;
const BUILDING_SIBLING = /^(?:ccn|beds|city|zip|npi|address|facility_?id|building_?id)$/i;

type Sink = (names: string[]) => void;
let sink: Sink | null = null;
const recent: unknown[] = [];
const RECENT = 60;
const early: string[] = [];

/** Names found in one server answer (person fields only, and each first name). */
export function namesIn(data: unknown): string[] {
  const found: string[] = [];
  let budget = 5000;
  const walk = (v: unknown, depth: number) => {
    if (budget-- <= 0 || depth > 8 || v === null || typeof v !== 'object') return;
    if (Array.isArray(v)) {
      for (const x of v) walk(x, depth + 1);
      return;
    }
    const o = v as Record<string, unknown>;
    const keys = Object.keys(o);
    const personish = keys.some((k) => PERSON_SIBLING.test(k));
    const buildingish = !personish && keys.some((k) => BUILDING_SIBLING.test(k));
    for (const k of keys) {
      const x = o[k];
      if (typeof x === 'string') {
        if (BUILDING_KEY.test(k) || (buildingish && k === 'name')) {
          const t = x.trim();
          if (t) found.push(t);
          // Screens drop a leading "The" ("The Arbour at Westminster Manor" -> "Arbour at ...").
          if (/^The \S/.test(t)) found.push(t.slice(4));
        } else if (PERSON_KEY.test(k) || (personish && k === 'name')) {
          const t = x.trim();
          if (!t) continue;
          found.push(t);
          const first = t.split(/\s+/)[0];
          if (first && first !== t && first.length > 1 && !/\.$/.test(first)) found.push(first);
        }
      } else walk(x, depth + 1);
    }
  };
  walk(data, 0);
  return found;
}

/** Protect these names (now, or as soon as the Spanish engine loads). */
export function protectNames(names: Iterable<string | null | undefined>): void {
  const list: string[] = [];
  for (const n of names) if (typeof n === 'string' && n.trim()) list.push(n.trim());
  if (!list.length) return;
  if (sink) sink(list);
  else {
    early.push(...list);
    if (early.length > 5000) early.splice(0, early.length - 5000);
  }
}

/** Walk one answer now and protect the names in it. */
export function harvestNames(data: unknown): void {
  protectNames(namesIn(data));
}

/** The API client's hook: cheap while English is on, a walk once Spanish is loaded. */
export function noteAnswer(data: unknown): void {
  if (data === null || typeof data !== 'object') return;
  if (sink) {
    harvestNames(data);
    return;
  }
  recent.push(data);
  if (recent.length > RECENT) recent.shift();
}

/** Where names go once the engine exists (i18n/index.ts sets it on load). */
export function setNameSink(s: Sink | null): void {
  sink = s;
}

/** Hand the engine everything noted before it loaded. */
export function flushNames(): void {
  if (!sink) return;
  const list = early.splice(0);
  for (const d of recent.splice(0)) list.push(...namesIn(d));
  if (list.length) sink(list);
}

export function __resetNamesForTests(): void {
  sink = null;
  recent.length = 0;
  early.length = 0;
}
